import {
  INDIVIDUAL_ROW_LABEL,
  INDIVIDUAL_ROW_NAME,
  type TEntityPlanRow,
  type TSelectableEntityType,
} from '@verifime/components';
import type { TPlanIdentity } from './entityPlan';

/**
 * The wallet side of the confirm plan: which identity stands for the Individual, how a status
 * reads as a row, and which identity (if any) the customer chose for the entity. Pure.
 */

/** The wallet identity the customer chose on the choose screen, as the URL carries it. */
export type TPlanChosen = {
  /** The wallet identity chosen. Absent when the customer chose a new one. */
  identityId?: string;
};

/** The person the confirmation is about: the Individual in the wallet, if there is one. */
export type TPlanPerson = {
  /** The Individual's name; empty when the wallet has none or it is blank. */
  name: string;
  /** True when the wallet holds an Individual identity, Draft included (618 L8, W15). */
  isEstablished: boolean;
};

/** What the plan does with the entity the customer chose. */
export type TEntityPick = {
  entityType: TSelectableEntityType;
  row: TEntityPlanRow;
} & (
  | { kind: 'new' }
  | { kind: 'held'; identityId: string }
  | { kind: 'draft'; identityId: string }
);

const INDIVIDUAL_ENTITY_TYPES: readonly string[] = ['Individual_Domestic', 'Individual_Foreign'];

/**
 * How an identity status reads as a row. Rejected counts as submitted (#609 Q6). A status the plan
 * does not know is not here: it shows as to be submitted, and is never chosen.
 */
const ROW_STATUS: ReadonlyMap<string, TEntityPlanRow['status']> = new Map([
  ['Verified', 'verified'],
  ['InProgress', 'submitted'],
  ['Rejected', 'submitted'],
  ['Draft', 'draft'],
]);

export const rowStatus = (identity: TPlanIdentity | undefined): TEntityPlanRow['status'] =>
  ROW_STATUS.get(identity?.entityStatus ?? '') ?? 'toBeSubmitted';

/** "InProgress or later": the identity has been submitted, whatever the outcome since. */
export const isSubmitted = (identity: TPlanIdentity | undefined) =>
  ['verified', 'submitted'].includes(rowStatus(identity));

/** Creation time in epoch ms; an absent or unparseable date counts as the earliest. */
const createdAt = (identity: TPlanIdentity) => {
  const time = Date.parse(identity.createdDate ?? '');
  return Number.isNaN(time) ? Number.NEGATIVE_INFINITY : time;
};

/** The most recently created identity matching the predicate, if any. */
const latestIdentity = (
  identities: TPlanIdentity[],
  matches: (identity: TPlanIdentity) => boolean,
): TPlanIdentity | undefined =>
  identities
    .filter(matches)
    .reduce<
      TPlanIdentity | undefined
    >((latest, next) => (!latest || createdAt(next) > createdAt(latest) ? next : latest), undefined);

/** The customer's own identity: the most recently created Individual, Draft included. */
export const latestIndividual = (identities: TPlanIdentity[]) =>
  latestIdentity(identities, (identity) =>
    INDIVIDUAL_ENTITY_TYPES.includes(identity.entityType ?? ''),
  );

const nameOr = (identity: TPlanIdentity | undefined, fallback: string) =>
  identity?.customerName?.trim() || fallback;

/** Whether the identity is in the set already shared with this client; false when not read. */
const isShared = (
  identity: TPlanIdentity | undefined,
  sharedIdentityIds: ReadonlySet<string> | undefined,
): boolean => !!sharedIdentityIds?.has(identity?.id);

/** The row says it is already shared when its identity is; otherwise it is left as it was (#665). */
const markedIfShared = (row: TEntityPlanRow, isIdentityShared: boolean): TEntityPlanRow =>
  isIdentityShared ? { ...row, alreadyShared: true } : row;

export const individualRow = (
  individual: TPlanIdentity | undefined,
  sharedIdentityIds?: ReadonlySet<string>,
): TEntityPlanRow =>
  markedIfShared(
    {
      label: INDIVIDUAL_ROW_LABEL,
      name: nameOr(individual, INDIVIDUAL_ROW_NAME),
      status: rowStatus(individual),
    },
    isShared(individual, sharedIdentityIds),
  );

export const personOf = (individual: TPlanIdentity | undefined): TPlanPerson => ({
  name: individual?.customerName?.trim() ?? '',
  isEstablished: !!individual,
});

/** An entity row: its type, with a name only when there is one (the type alone otherwise). */
const entityRow = (
  label: string,
  name: string | undefined,
  status: TEntityPlanRow['status'],
): TEntityPlanRow => (name ? { label, name, status } : { label, status });

/**
 * The organisation name the partner declared, exactly as given: plain text, never trimmed or
 * re-validated (person-api omits a name that fails its name rule). A blank one is no name.
 */
const declaredNameOf = (name: string | undefined) => (name?.trim() ? name : undefined);

type TPickInput = {
  entityType: TSelectableEntityType;
  entityLabel: string;
  chosen?: TPlanChosen;
  identities: TPlanIdentity[];
  declaredOrganisationName?: string;
  /** The ids already shared with this client; absent when that was not read. */
  sharedIdentityIds?: ReadonlySet<string>;
};

/**
 * A new entity: the type, and the declared name when there is one (#618 R10). Also the row of a
 * resumed screen, where nothing was chosen: Continue starts a new entity's wizard, so the row says
 * a new entity, never the name or status of a wallet identity the wizard would not touch.
 */
function newPick({ entityType, entityLabel, declaredOrganisationName }: TPickInput): TEntityPick {
  const row = entityRow(entityLabel, declaredNameOf(declaredOrganisationName), 'toBeSubmitted');
  return { kind: 'new', entityType, row };
}

/**
 * The wallet identity the customer chose, when it is one the plan can show: it is in the wallet,
 * of the entity type the plan is for, and has a status the plan knows.
 */
export function chosenIdentity({
  chosen,
  entityType,
  identities,
}: Pick<TPickInput, 'chosen' | 'entityType' | 'identities'>): TPlanIdentity | undefined {
  const identityId = chosen?.identityId;
  return identities.find(
    (identity) =>
      !!identityId &&
      identity.id === identityId &&
      identity.entityType === entityType &&
      ROW_STATUS.has(identity.entityStatus ?? ''),
  );
}

/**
 * A chosen Draft is resumed; any other chosen identity is shared as it is, unless it is shared with
 * this client already: its row then says so, and the action offers no share for it.
 */
function walletPick(input: TPickInput, identity: TPlanIdentity, identityId: string): TEntityPick {
  const { entityType, entityLabel, sharedIdentityIds } = input;
  const status = rowStatus(identity);
  const row = markedIfShared(
    entityRow(entityLabel, identity.customerName?.trim(), status),
    isShared(identity, sharedIdentityIds),
  );
  return { kind: status === 'draft' ? 'draft' : 'held', entityType, identityId, row };
}

/**
 * What the customer chose for the entity, read against the wallet. A chosen option with no
 * identity, or one the wallet cannot back, is a new entity: never a guess at another identity.
 */
export function pickEntity(input: TPickInput): TEntityPick {
  const identityId = input.chosen?.identityId;
  const identity = chosenIdentity(input);
  return identityId && identity ? walletPick(input, identity, identityId) : newPick(input);
}

/**
 * The wallet identities that might be shared with the client, whose permissions are read to learn
 * which already are: the customer's own Individual, and the wallet identity chosen. An id the
 * wallet does not hold is never asked about, whatever the URL carries.
 */
export function identityIdsToCheck(
  identities: TPlanIdentity[],
  chosen: TPlanChosen | undefined,
): string[] {
  const candidates = [
    latestIndividual(identities)?.id,
    identities.find((identity) => !!chosen?.identityId && identity.id === chosen.identityId)?.id,
  ];
  return candidates.filter((id): id is string => !!id);
}

/**
 * False when the customer chose a wallet identity the wallet cannot back for this entity type (not
 * held, another type, or a status the plan does not know). The plan then treats the choice as a
 * new entity. True when no wallet identity was chosen: there is nothing to back.
 */
export function isChosenIdentityBacked(
  identities: TPlanIdentity[],
  chosen: TPlanChosen | undefined,
  entityType: TSelectableEntityType,
): boolean {
  return !chosen?.identityId || !!chosenIdentity({ chosen, entityType, identities });
}
